"use client";

import React, { useEffect, useRef, useState } from "react";

interface MultiSelectProps {
  value: string[];
  onChange: (value: string[]) => void;
  options: string[];
  placeholder: string;
  itemSuffix?: string;
}

const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="shrink-0 text-gray-400">
    <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
    <path d="M20 20l-3-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const Chevron = ({ open }: { open: boolean }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    className={`shrink-0 text-gray-500 transition-transform ${open ? "rotate-180" : ""}`}
  >
    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CheckMark = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="text-white">
    <path d="M5 12l4.5 4.5L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const MultiSelect: React.FC<MultiSelectProps> = ({
  value,
  onChange,
  options,
  placeholder,
  itemSuffix,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const filtered = options.filter((o) =>
    o.toLowerCase().includes(query.trim().toLowerCase())
  );

  const toggle = (option: string) => {
    if (value.includes(option)) {
      onChange(value.filter((v) => v !== option));
    } else {
      onChange([...value, option]);
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      {/* Trigger / search box */}
      <div
        className="flex items-center gap-2 w-full p-3 border border-gray-200 rounded-xl bg-white shadow-sm cursor-pointer min-h-[50px]"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? (
          <>
            <SearchIcon />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              placeholder={placeholder}
              className="flex-1 min-w-0 outline-none bg-transparent text-gray-900 placeholder:text-gray-400"
            />
          </>
        ) : value.length > 0 ? (
          <div className="flex-1 min-w-0 flex flex-wrap gap-1.5">
            {value.map((v) => (
              <span
                key={v}
                className="inline-flex items-center px-2.5 py-0.5 rounded-md bg-blue-50 text-blue-600 text-sm font-medium"
              >
                {v}
              </span>
            ))}
          </div>
        ) : (
          <span className="flex-1 min-w-0 truncate text-gray-400">{placeholder}</span>
        )}
        <Chevron open={open} />
      </div>

      {/* Options panel */}
      {open && (
        <div className="absolute left-0 right-0 top-full mt-2 z-20 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
          <div className="max-h-64 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-5 py-4 text-gray-400 text-sm">Ничего не найдено</div>
            ) : (
              filtered.map((option, idx) => {
                const checked = value.includes(option);
                return (
                  <button
                    type="button"
                    key={option}
                    onClick={() => toggle(option)}
                    className={`flex items-center gap-3 w-full px-5 py-4 text-left transition-colors hover:bg-gray-50 ${
                      idx !== filtered.length - 1 ? "border-b border-gray-100" : ""
                    }`}
                  >
                    <span
                      className={`w-6 h-6 rounded-lg border flex items-center justify-center shrink-0 transition-colors ${
                        checked ? "bg-blue-600 border-blue-600" : "bg-white border-gray-300"
                      }`}
                    >
                      {checked && <CheckMark />}
                    </span>
                    <span className="text-gray-900 font-medium">{option}</span>
                    {itemSuffix && <span className="text-gray-400">{itemSuffix}</span>}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelect;
